import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Save,
  X,
  Loader2,
  FileText,
  Eye,
} from 'lucide-react'
import axios from 'axios'
import { outreachTemplatesApi, type OutreachTemplate, type OutreachTemplateUpdate } from '@/services/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { useSetBreadcrumbLabel } from '@/contexts/BreadcrumbContext'

const TEMPLATE_VARIABLES = [
  { name: 'greeting_name', description: 'Contact name or fallback greeting' },
  { name: 'companyName', description: 'Company name' },
  { name: 'personalizedIntro', description: 'AI-generated personalized intro' },
  { name: 'specificBenefit', description: 'Benefit tailored to the prospect' },
  { name: 'callToActionLink', description: 'Link for the call to action' },
]

function highlightVariables(text: string): React.ReactNode {
  if (!text) return null
  const pattern = /\{\{\s*(\w+)\s*\}\}/g
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match
  while ((match = pattern.exec(text)) !== null) {
    parts.push(text.slice(lastIndex, match.index))
    parts.push(
      <span
        key={match.index}
        className="rounded bg-amber-100 px-1 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
      >
        {match[0]}
      </span>
    )
    lastIndex = pattern.lastIndex
  }
  parts.push(text.slice(lastIndex))
  return <>{parts}</>
}

export default function OutreachTemplateDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const setBreadcrumbLabel = useSetBreadcrumbLabel()
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState<OutreachTemplateUpdate>({})
  const [updateError, setUpdateError] = useState<string | null>(null)

  const { data: template, isLoading, error } = useQuery<OutreachTemplate>({
    queryKey: ['outreach-template', id],
    queryFn: () => outreachTemplatesApi.get(id!),
    enabled: !!id,
  })

  useEffect(() => {
    if (template?.name) {
      setBreadcrumbLabel('templateName', template.name)
    }
    return () => setBreadcrumbLabel('templateName', undefined)
  }, [template?.name, setBreadcrumbLabel])

  const updateMutation = useMutation({
    mutationFn: (data: OutreachTemplateUpdate) => outreachTemplatesApi.update(id!, data),
    onSuccess: (updatedData) => {
      queryClient.setQueryData(['outreach-template', id], updatedData)
      queryClient.invalidateQueries({ queryKey: ['outreach-templates'] })
      setUpdateError(null)
      setIsEditing(false)
    },
    onError: (error) => {
      console.error('Failed to update template:', error)
      const message = axios.isAxiosError(error)
        ? error.response?.data?.detail || error.message
        : error instanceof Error
          ? error.message
          : 'Failed to update template'
      setUpdateError(message)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => outreachTemplatesApi.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['outreach-templates'] })
      navigate('/app/outreach/templates')
    },
  })

  const handleEdit = () => {
    if (template) {
      setUpdateError(null)
      setFormData({
        name: template.name,
        role_label: template.role_label,
        subject: template.subject,
        html_body: template.html_body,
        text_body: template.text_body,
        is_default: template.is_default,
        is_active: template.is_active,
      })
      setIsEditing(true)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
    setFormData({})
    setUpdateError(null)
  }

  const handleSave = () => {
    updateMutation.mutate(formData)
  }

  const handleInputChange = (key: keyof OutreachTemplateUpdate, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-64" />
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error || !template) {
    const errorMessage = axios.isAxiosError(error)
      ? error.response?.data?.detail || error.message
      : error instanceof Error
        ? error.message
        : null

    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/app/outreach/templates')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Templates
        </Button>
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {errorMessage || 'Template not found'}
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/outreach/templates')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{template.name}</h1>
            <div className="mt-1 flex items-center gap-2">
              <Badge variant="secondary">{template.role_label}</Badge>
              {template.is_default && (
                <Badge variant="outline">Default</Badge>
              )}
              {!template.is_active && (
                <Badge variant="destructive">Inactive</Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={handleCancel} disabled={updateMutation.isPending}>
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleEdit}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Template</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete &quot;{template.name}&quot;? This action cannot
                      be undone. Existing outreach records that used this template will keep their
                      content but lose the template link.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => deleteMutation.mutate()}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {deleteMutation.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : null}
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
      </div>
      {updateError && (
        <p className="text-sm text-destructive">{updateError}</p>
      )}

      {/* Template Details Card */}
      <Card>
        <CardHeader>
          <CardTitle>Template Details</CardTitle>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Template Name</Label>
                  <Input
                    id="name"
                    value={formData.name || ''}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter template name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role_label">Target Role / Goal</Label>
                  <Input
                    id="role_label"
                    value={formData.role_label || ''}
                    onChange={(e) => handleInputChange('role_label', e.target.value)}
                    placeholder="e.g., Buyer, Seller, Trader"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="subject">Email Subject</Label>
                <Input
                  id="subject"
                  value={formData.subject || ''}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  placeholder="Enter email subject"
                />
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_active"
                    checked={formData.is_active ?? true}
                    onCheckedChange={(checked) => handleInputChange('is_active', checked)}
                  />
                  <Label htmlFor="is_active">Active</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_default"
                    checked={formData.is_default ?? false}
                    onCheckedChange={(checked) => handleInputChange('is_default', checked)}
                  />
                  <Label htmlFor="is_default">Default Template</Label>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              <DetailItem label="Template Name" value={template.name} />
              <DetailItem label="Target Role / Goal" value={template.role_label} />
              <div className="md:col-span-2">
                <DetailItem label="Email Subject" value={template.subject} />
              </div>
              <DetailItem
                label="Status"
                value={template.is_active ? 'Active' : 'Inactive'}
              />
              <DetailItem
                label="Default"
                value={template.is_default ? 'Yes' : 'No'}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Content Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Email Content
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <div className="space-y-2">
              <Label htmlFor="text_body">Email Body</Label>
              <Textarea
                id="text_body"
                value={formData.text_body || ''}
                onChange={(e) => handleInputChange('text_body', e.target.value)}
                rows={16}
                className="font-mono text-sm"
                placeholder="Enter email body content..."
              />
              <p className="text-xs text-muted-foreground">
                Use variables like {'{{ companyName }}'} to personalize the email.
              </p>
            </div>
          ) : (
            <div className="rounded-lg border bg-muted/30 p-4">
              <pre className="whitespace-pre-wrap font-mono text-sm text-foreground">
                {highlightVariables(template.text_body || template.html_body.replace(/<[^>]*>/g, ''))}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Template Variables Reference Card */}
      <Card>
        <CardHeader>
          <CardTitle>Available Variables</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {TEMPLATE_VARIABLES.map((variable) => (
              <div
                key={variable.name}
                className="rounded-lg border p-3"
              >
                <code className="text-sm font-medium text-amber-600 dark:text-amber-400">
                  {'{{ ' + variable.name + ' }}'}
                </code>
                <p className="mt-1 text-xs text-muted-foreground">
                  {variable.description}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Metadata Card */}
      <Card>
        <CardHeader>
          <CardTitle>Record Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-3">
            <DetailItem label="Template ID" value={template.id} />
            <DetailItem
              label="Created At"
              value={template.created_at ? new Date(template.created_at).toLocaleString() : undefined}
            />
            <DetailItem
              label="Last Updated"
              value={template.updated_at ? new Date(template.updated_at).toLocaleString() : undefined}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

interface DetailItemProps {
  label: string
  value: string | null | undefined
}

function DetailItem({ label, value }: DetailItemProps) {
  return (
    <div>
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      <p className="mt-1 text-foreground">{value || '-'}</p>
    </div>
  )
}
